import { useState, useEffect, useRef } from 'react';
import { AppShell } from '../../components/layout/AppShell';
import { Card } from '../../components/ui/Card';
import { Table } from '../../components/ui/Table';
import { Combobox } from '../../components/ui/Combobox';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { Alert } from '../../components/ui/Alert';
import { EmptyState } from '../../components/ui/EmptyState';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Toast } from '../../components/ui/Toast';
import { KpiCard } from '../../components/kpi/KpiCard';
import { MultiLineChart } from '../../components/charts/MultiLineChart';
import { DriverBars } from '../../components/charts/DriverBars';
import { ProductPicker } from '../../components/charts/ProductPicker';
import { FilterBar } from '../../components/filters/FilterBar';
import { PeriodToggle } from '../../components/filters/PeriodToggle';
import { RangeFilters } from '../../components/filters/RangeFilters';
import { useApiData } from '../../hooks/useApiData';
import { useOptions } from '../../hooks/useOptions';
import { useDataRefresh } from '../../context/DataRefreshContext';
import { formatNumber, formatPercent } from '../../utils/formatters';
import { csvCell, downloadText } from '../../utils/exportCsv';
import { colorForProduct } from '../../config/productColors';
import { dayTickLabel, periodDisplay, rangeForGranularity } from '../../utils/periods';
import { api } from '../../lib/api';

// Compact panel shown when a Resellers row is tapped: which products drive this reseller,
// ranked by their avg/day (active-days based, paginated), from the reseller × product matrix.
function ResellerMiniDetail({ products }) {
  const items = (products || []).map((p) => ({ name: p.name, value: p.total }));
  return <DriverBars items={items} countLabel="Products used" topLabel="Top product" colorOf={(i) => colorForProduct(i.name)} />;
}

const EMPTY_RESELLER_FORM = { name: '', families: [] };

// "Add Reseller" — registers a new row in BigQuery's dim_resellers, scoped to the
// product families they'll sell, so it shows up in the options list and dashboards
// from the next data run onward.
function AddResellerModal({ open, onClose, families, onCreated }) {
  const [form, setForm] = useState(EMPTY_RESELLER_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { if (open) { setForm(EMPTY_RESELLER_FORM); setError(''); } }, [open]);

  const toggleFamily = (f) => setForm((cur) => ({
    ...cur,
    families: cur.families.includes(f) ? cur.families.filter((x) => x !== f) : [...cur.families, f],
  }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await api.post('/api/catalog/resellers', { name: form.name, families: form.families });
      onCreated(form.name);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const allSelected = form.families.length === families.length;

  return (
    <Modal open={open} onClose={onClose} title="Add reseller" size="lg">
      <form onSubmit={submit} className="space-y-4">
        <p className="text-xs leading-relaxed text-slate-500 dark:text-slate-400">
          Adds this reseller so it appears here and in reporting from the next data run onward.
        </p>
        <Input label="Reseller name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required autoFocus />
        <div>
          <div className="mb-1.5 flex items-center justify-between gap-2">
            <span className="text-sm font-medium text-slate-600 dark:text-slate-300">
              Product families <span className="font-normal text-slate-400">(optional)</span>
            </span>
            <div className="flex items-center gap-2.5 text-xs">
              <button type="button" onClick={() => setForm((c) => ({ ...c, families: [...families] }))} disabled={allSelected} className="font-medium text-brand hover:text-brand-dark disabled:text-slate-300 disabled:dark:text-slate-600">
                Select all
              </button>
              <span className="text-slate-300 dark:text-slate-600">·</span>
              <button type="button" onClick={() => setForm((c) => ({ ...c, families: [] }))} disabled={!form.families.length} className="font-medium text-brand hover:text-brand-dark disabled:text-slate-300 disabled:dark:text-slate-600">
                Clear
              </button>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5 rounded-lg border border-slate-300 dark:border-white/15 bg-white dark:bg-slate-800 p-2.5 max-h-40 overflow-y-auto">
            {families.map((f) => {
              const on = form.families.includes(f);
              return (
                <button
                  key={f}
                  type="button"
                  onClick={() => toggleFamily(f)}
                  className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium transition ${
                    on
                      ? 'bg-brand text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-white/10 dark:text-slate-300 dark:hover:bg-white/15'
                  }`}
                >
                  {on && (
                    <svg className="h-3 w-3" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M5 10l3 3 7-7" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  )}
                  {f}
                </button>
              );
            })}
          </div>
        </div>
        {error && <Alert>{error}</Alert>}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving || !form.name.trim()}>
            {saving ? 'Adding…' : 'Add reseller'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export default function Resellers() {
  const { prefetch, refresh } = useDataRefresh();
  const { resellers, productFamilies: families, dateRange } = useOptions();
  const [addOpen, setAddOpen] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (!msg) return undefined;
    const t = setTimeout(() => setMsg(null), 4500);
    return () => clearTimeout(t);
  }, [msg]);

  const onResellerCreated = (name) => {
    setAddOpen(false);
    refresh();
    setMsg({ type: 'success', title: 'Reseller added', text: `"${name}" has been added.` });
  };
  const [reseller, setReseller] = useState('');
  const [granularity, setGranularity] = useState('day');
  const [range, setRange] = useState({ from: '', to: '', preset: '' });

  // Switching grouping also re-frames the date window — see rangeForGranularity.
  const changeGranularity = (g) => {
    setGranularity(g);
    const next = rangeForGranularity(g, dateRange);
    if (next) setRange(next);
  };
  const { from, to } = range;

  // Default to the LAST 7 DAYS (Daily grouping) on first load, so the per-product
  // line chart opens on a week of trend. One-time; the user can change it after.
  const initRange = useRef(false);
  useEffect(() => {
    if (!initRange.current && dateRange?.max) {
      const [y, mo, d] = dateRange.max.split('-').map(Number);
      const start = new Date(y, mo - 1, d - 6);
      const pad = (n) => String(n).padStart(2, '0');
      const fromStr = `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}`;
      setRange({ from: fromStr, to: dateRange.max, preset: '' });
      initRange.current = true;
    }
  }, [dateRange]);

  // Detailed CSV: filters, summary, family breakdown, per-product breakdown, series.
  const doExport = (data) => {
    if (!data) return;
    const lines = [];
    const row = (a) => lines.push(a.map(csvCell).join(','));
    row(['CysOps Reseller Analysis']);
    row(['Reseller', reseller || 'All']);
    row(['Grouping', granularity]);
    row(['Range', from || to ? `${from || 'start'} to ${to || 'end'}` : 'All time']);
    row(['Generated', new Date().toLocaleString()]);
    lines.push('');
    row(['Summary']);
    row(['Total Usage', data.stats.total]);
    lines.push('');
    row(['Products']);
    row(['Product', 'Product family', 'Usage', 'Share %']);
    (data.byProduct || []).forEach((p) => row([p.name, p.family, p.total, p.share]));
    lines.push('');
    row(['Usage Over Time']);
    row(['Period', 'Usage']);
    (data.series || []).forEach((s) => row([s.period, s.total]));
    const rn = reseller ? reseller.replace(/[^\w-]+/g, '-') : 'all';
    const rng = from || to ? `${from || 'start'}_${to || 'end'}` : 'all-time';
    downloadText(`cysops-reseller-analysis_${rn}_${rng}.csv`, lines.join('\n'), 'text/csv');
  };

  // Default to the first reseller once options are available.
  useEffect(() => {
    if (!reseller && resellers.length) setReseller(resellers[0]);
  }, [reseller, resellers]);

  const { data, loading, error } = useApiData('/api/data/resellers', {
    reseller, granularity, from, to,
  });

  // Once the current view has loaded, predictively warm each reseller (current
  // grouping + period) in the background so the next selection is instant. Gated
  // on !loading + capped concurrency so it never slows the active view.
  useEffect(() => {
    if (loading) return;
    resellers.forEach((r) =>
      prefetch('/api/data/resellers', { reseller: r, granularity, from, to }));
  }, [resellers, granularity, from, to, loading, prefetch]);

  // Pivot the period × product detail into a per-period table for this reseller:
  // one row per period (day / month / year, per the grouping), a column per product,
  // plus a Total — so each product's usage is visible for each day / month / year.
  const detail = data?.detail || [];
  const ppTotals = {};
  detail.forEach((d) => { ppTotals[d.product] = (ppTotals[d.product] || 0) + d.total; });
  const ppProducts = Object.keys(ppTotals).sort((a, b) => ppTotals[b] - ppTotals[a]);
  const ppRowsMap = new Map();
  detail.forEach((d) => {
    if (!ppRowsMap.has(d.period)) ppRowsMap.set(d.period, { period: d.period, total: 0, cells: {} });
    const r = ppRowsMap.get(d.period);
    r.cells[d.product] = d.total;
    r.total += d.total;
  });
  const ppRows = [...ppRowsMap.values()].sort((a, b) => (a.period < b.period ? -1 : 1));
  const periodLabel = ({ day: 'Daily', month: 'Monthly', year: 'Yearly' })[granularity] || 'Per-Period';

  // One colour-coded line per product for this reseller (raw per-day values), with a
  // picker (default top 5, or all if fewer) reset when the reseller changes; colours fixed per product.
  const colorFor = {};
  ppProducts.forEach((n) => { colorFor[n] = colorForProduct(n); });
  const chartData = ppRows.map((r) => ({ period: r.period, ...r.cells }));
  const [selProducts, setSelProducts] = useState([]);
  const [activeProduct, setActiveProduct] = useState(null);
  const initedReseller = useRef(null);
  const lastData = useRef(null);
  useEffect(() => {
    // `data` lags one render behind a reseller switch — for a moment ppProducts
    // still reflects the PREVIOUS reseller while `reseller` already points at the
    // new one. Gate on `data` actually changing so we never lock in a top-5 picked
    // from stale products (which would then never get corrected once the real
    // data for the new reseller arrives, since initedReseller.current already
    // matched).
    if (data === lastData.current) return;
    lastData.current = data;
    if (ppProducts.length && initedReseller.current !== reseller) {
      setSelProducts(ppProducts.slice(0, 5));
      initedReseller.current = reseller;
    }
  }, [data, ppProducts, reseller]);
  const seriesToDraw = ppProducts.filter((n) => selProducts.includes(n));
  const toggleProduct = (n) => setSelProducts((c) => (c.includes(n) ? c.filter((x) => x !== n) : [...c, n]));

  return (
    <AppShell title="Reseller Analysis">
      <FilterBar>
        <div className="w-full sm:w-56">
          <Combobox
            label="Reseller"
            value={reseller}
            onChange={setReseller}
            options={resellers}
            searchPlaceholder="Search reseller…"
          />
        </div>
        <div>
          <span className="block mb-1 text-sm font-medium text-slate-600 dark:text-slate-300">Grouping</span>
          <PeriodToggle value={granularity} onChange={changeGranularity} />
        </div>
        <RangeFilters from={from} to={to} preset={range.preset} dateRange={dateRange} onChange={setRange} />
        <div className="ml-auto flex gap-2">
          <Button onClick={() => setAddOpen(true)} className="gap-1.5">
            <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M10 4v12M4 10h12" strokeLinecap="round" /></svg>
            Add reseller
          </Button>
          <Button variant="secondary" disabled={!data} onClick={() => doExport(data)}>
            Export CSV
          </Button>
        </div>
      </FilterBar>

      {loading && <Spinner />}
      {error && <Alert>{error}</Alert>}
      {data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <KpiCard label="Total Usage" value={formatNumber(data.stats.total)} sub="avg / day" />
            <KpiCard label="Best Period" value={periodDisplay(data.stats.bestPeriod, granularity)} sub={formatNumber(data.stats.bestPeriodValue)} />
            <KpiCard
              label="Top Reseller"
              value={data.allResellers?.[0]?.name || '—'}
              sub={data.allResellers?.[0] ? `${formatNumber(data.allResellers[0].total)} avg/day` : undefined}
            />
          </div>

          <Card
            title="Product Usage Over Time"
            actions={ppProducts.length ? (
              <ProductPicker
                products={ppProducts}
                selected={selProducts}
                colorFor={colorFor}
                onToggle={toggleProduct}
                onSelectAll={() => setSelProducts(ppProducts)}
                onClear={() => setSelProducts([])}
                onHover={setActiveProduct}
              />
            ) : null}
          >
            {chartData.length && seriesToDraw.length ? (
              <MultiLineChart
                data={chartData}
                series={seriesToDraw}
                colorFor={colorFor}
                activeSeries={activeProduct}
                xTickFormatter={granularity === 'day' ? dayTickLabel : undefined}
                note={granularity === 'day' ? undefined : 'average usage per active day'}
              />
            ) : <EmptyState />}
          </Card>

          <Card title={`${periodLabel} Breakdown`}>
            {ppRows.length ? (
              <Table
                columns={[
                  { key: 'period', label: 'Period', render: (v) => periodDisplay(v, granularity) },
                  ...ppProducts.map((pn) => ({
                    key: pn, label: pn, align: 'right', render: (v) => formatNumber(v || 0),
                  })),
                  { key: 'total', label: 'Total', align: 'right', render: (v) => formatNumber(v) },
                ]}
                rows={ppRows.map((r) => ({ period: r.period, total: r.total, ...r.cells }))}
                pageSize={12}
                searchable
                searchPlaceholder="Search period…"
              />
            ) : <EmptyState />}
          </Card>

          <Card title="Resellers" subtitle="Average per active day over the selected range.">
            {data.allResellers && data.allResellers.length ? (
              <Table
                columns={[
                  { key: 'name', label: 'Reseller' },
                  { key: 'total', label: 'Usage (avg/day)', align: 'right', render: (v) => formatNumber(v) },
                  { key: 'share', label: 'Share', align: 'right', render: (v) => formatPercent(v) },
                ]}
                rows={data.allResellers}
                pageSize={10}
                searchable
                searchPlaceholder="Search reseller…"
                rowKey={(r) => r.name}
                expandedContent={(r) => <ResellerMiniDetail products={data.resellerProducts?.[r.name]} />}
              />
            ) : <EmptyState />}
          </Card>
        </>
      )}

      <AddResellerModal open={addOpen} onClose={() => setAddOpen(false)} families={families} onCreated={onResellerCreated} />
      <Toast msg={msg} onDismiss={() => setMsg(null)} />
    </AppShell>
  );
}
